import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ContentType, YourAPPProductType } from "../shared/contentTypes";
import { NavigationDataModel, NavigationViewModel, NavigationTitleViewModel, UserProfileNavigationViewModel } from "../shared/models/navigation.model";
import { CoreLogService } from "./log.service";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { IObjectObserver } from "./IObjectObserver";
import { ObjectObserverFactory } from "./objectObserverFactory";
import { NavigationHelperService } from "./navigationHelper.service";
import { environment } from "../../../environments/environment";

@Injectable({
  providedIn: "root"
})
export class NavigationService {

  private _navigationObsServ: IObjectObserver<NavigationDataModel>;

  constructor(private objObsInjectHelper: CircularInjectionHelper
    , private _navigationHelperService: NavigationHelperService) {
    const me = this;
    me._navigationObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<NavigationDataModel>(ContentType.Navigation, objObsInjectHelper);
  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._navigationObsServ.ObserveChanges;
  }

  public GetActiveNavigation(): Promise<NavigationViewModel[]> {
    const me = this;
    return new Promise<NavigationViewModel[]>((resolve, reject) => {
      me._navigationObsServ.GetObjectsByFilter((navigation) => navigation.release && me.isContenttypeSupported(navigation.content_type))
        .then((navigations) => {
          navigations.sort((left, right) => (left.sort_nr < right.sort_nr ? -1 : 1));
          resolve(me.mapToNavigationViewModelList(navigations));
        })
        .catch((error) => { me.objObsInjectHelper.coreLogService.error("MainnavService.GetActiveNavigation", "GetObjectsByFilter", error); reject(); });
    });
  }

  public GetRouteNavigationTitleViewModel(navigationViewModel: NavigationViewModel): NavigationTitleViewModel {
    const me = this;
    try {
      return me.mapToNavigationtitleViewModel(navigationViewModel);
    } catch (error) {
      me.objObsInjectHelper.coreLogService.error("MainnavService.GetRouteNavigationTitle", "mapToNavigationtitleViewModel", error);
      return null;
    }
  }

  public async GetRouteNavigationTitleViewModelForContentType(contenttype: ContentType): Promise<NavigationTitleViewModel | null> {
    const me = this;
    try {
      const navigation = await me._navigationObsServ.GetObject(contenttype);
      if (navigation) {
        const result: NavigationTitleViewModel = new NavigationTitleViewModel()
        result.labelDE = navigation.label_de;
        result.labelEN = navigation.label_en;
        return result;
      }
      me.objObsInjectHelper.coreLogService.error("MainnavService.GetRouteNavigationTitleViewModelForContentType", "No Navigation found");
      return null;
    } catch (error) {
      me.objObsInjectHelper.coreLogService.error("MainnavService.GetRouteNavigationTitleViewModelForContentType", "_navigationObsServ.GetObject", error);
      return null;
    }
  }

  public UserProfileNavigation(): Promise<UserProfileNavigationViewModel[]> {
    const me = this;
    return new Promise<UserProfileNavigationViewModel[]>((resolve, reject) => {
      me._navigationObsServ.GetObjectsByFilter((navigation) => navigation.release
        && (navigation.content_type === ContentType.WebVoucher || navigation.content_type === ContentType.Bonussystem))
        .then((navigations) => {
          if (navigations && navigations.length > 0) {
            resolve(me.mapToUserProfileNavigationViewModelList(navigations));
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          me.objObsInjectHelper.coreLogService.error("MainnavService.UserProfileNavigation", "GetObjectsByFilter", error);
          reject();
        });
    });
  }

  public GetNavigationPathForContentType(content_type: ContentType): string {
    switch (content_type) {
      case ContentType.Home_Slider:
        return "home";
      case ContentType.Location:
        return "location";
      case ContentType.News:
        return "news";
      case ContentType.Event:
        return "event";
      case ContentType.Coupon:
        return "coupon";
      case ContentType.Reservation:
        return "reservation";
      case ContentType.WebVoucher:
        return "webvoucherdetail";
      case ContentType.Bonussystem:
        return "bonusaction";
      case ContentType.Menu:
        return "menugroup";
      case ContentType.Impressum:
        return "termcondition";
      case ContentType.ExternalLink:
        return "externallink";
      default:
        // todo log unknown content type
        return "none";
    }
  }

  // TODO IMPORTANT!!!!
  public GetSvgNameForContentType(content_type: ContentType): string {
    switch (content_type) {
      case ContentType.Home_Slider:
        return "hypersoft-start";
      case ContentType.Location:
        return "hypersoft-standort";
      case ContentType.News:
        return "hypersoft-news";
      case ContentType.Event:
        return "hypersoft-events";
      case ContentType.Coupon:
        return "hypersoft-coupon";
      case ContentType.Reservation:
        return "reservation";
      case ContentType.WebVoucher:
        return "hypersoft-gutschein";
      case ContentType.Bonussystem:
        return "hypersoft-bonus";
      case ContentType.Menu:
        return "hypersoft-menu";
      case ContentType.Impressum:
        return "hypersoft-impressum";
      case ContentType.ExternalLink:
        return "hypersoft-extern";
      default:
        // todo log unknown content type
        return "none";
    }
  }

  /** Ermittelt, ob der Navigations Punkt zugelassen wurde, prüft aber nicht ob es Unterstützt wird, dazu ist isContenttypeSupported */
  public async isContenttypeActive(ct: ContentType): Promise<boolean> {
    const me = this;
    const navigations = await me._navigationObsServ.GetObjectsByFilter((navigation) => navigation.release && navigation.content_type === ct);
    return navigations.length > 0;
  }

  /** Ermittelt, ob der Contenttype als Navigation unterstützt wird */
  public isContenttypeSupported(ct: ContentType | -1): boolean {
    const me = this;
    switch (environment.producttype) {
      case YourAPPProductType.emenu: {
        return (ct === -1 // No Linked Content
          || ct === ContentType.Home_Slider
          || ct === ContentType.Home_PrioTile
          || ct === ContentType.Home_Tile
          || ct === ContentType.ExternalLink
          || ct === ContentType.Menu
          || ct === ContentType.Item_Group
          || ct === ContentType.Impressum
          || ct === ContentType.Term);
      }
      case YourAPPProductType.webshop: {
        return (ct === -1 // No Linked Content
          || ct === ContentType.Home_Slider
          || ct === ContentType.Home_PrioTile
          || ct === ContentType.Home_Tile
          || ct === ContentType.ExternalLink
          || ct === ContentType.Location
          || ct === ContentType.Menu
          || ct === ContentType.Item_Group
          || ct === ContentType.Impressum
          || ct === ContentType.Term);
      }
      default: {
        return true;
      }
    }
  }

  private mapToNavigationViewModel(model: NavigationDataModel): NavigationViewModel {
    const me = this;
    const viewModel = new NavigationViewModel();
    viewModel.contentType = model.content_type;
    viewModel.order = model.sort_nr;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    viewModel.inSubview = model.sort_nr < 4;
    viewModel.navigationKey = me._navigationHelperService.getPathForContentType(model.content_type);
    viewModel.svgName = me.GetSvgNameForContentType(model.content_type);
    return viewModel;
  }

  private mapToNavigationViewModelList(models: NavigationDataModel[]): NavigationViewModel[] {
    const me = this;
    const viewModels = new Array<NavigationViewModel>();
    models.forEach(model => {
      viewModels.push(me.mapToNavigationViewModel(model));
    });
    return viewModels;
  }

  private mapToNavigationtitleViewModel(viewModel: NavigationViewModel): NavigationTitleViewModel {
    const titleViewModel = new NavigationTitleViewModel();
    if (viewModel) {
      titleViewModel.labelDE = viewModel.label_de;
      titleViewModel.labelEN = viewModel.label_en;
      return titleViewModel;
    } else {
      return null;
    }
  }

  private mapToUserProfileNavigationViewModelList(models: NavigationDataModel[]): UserProfileNavigationViewModel[] {
    const me = this;
    const viewModels = new Array<UserProfileNavigationViewModel>();
    models.forEach(model => {
      viewModels.push(me.mapToUserProfileNavigationViewModel(model));
    });
    return viewModels;
  }

  private mapToUserProfileNavigationViewModel(model: NavigationDataModel): UserProfileNavigationViewModel {
    const viewModel = new UserProfileNavigationViewModel();
    if (model) {
      viewModel.labelDE = model.label_de;
      viewModel.labelEN = model.label_en;
      viewModel.contentType = model.content_type;
      return viewModel;
    } else {
      return null;
    }
  }
}
